import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ContentFieldDto,
  DesignIterationDto,
  DesignTestInputDto,
} from '@models/job-request-iterations-dto.interface';
import { RouterLinkWithHref } from '@angular/router';

type ResultAlias =
  | 'density'
  | 'pycnometer'
  | 'rheology'
  | 'electricalStability'
  | 'hphtFluidLoss'
  | 'apiFluidLoss'
  | 'ppt'
  | 'retort'
  | 'psd'
  | 'salinity'
  | 'wbmTitration'
  | 'wbmSalinity'
  | 'staticAging'
  | 'hphtRheology';

type ResultValueMap = Map<string, string>;

interface TestResultFieldDef {
  label: string;
  keys?: string[];
  /** Fixed decimal places for numeric display. Omit to show the value as-is. */
  decimals?: number;
  compute?:
    | 'densityLbPerBbl'
    | 'retortOwr'
    | 'hphtFullFiltrate'
    | 'pptSpurt'
    | 'pptTotal'
    | 'pptValue'
    | 'salinityAlkalinity'
    | 'salinityChlorides'
    | 'salinityCalcium'
    | 'wbmChemistryTotalChlorides'
    | 'wbmTotalChlorides'
    | 'd10'
    | 'd50'
    | 'd90'
    | 'pycnometerAverageSg'
    | 'pycnometerAverageDensity'
    | 'pycnometerAverageAbsoluteVolume'
    | 'sagFactor'
    | 'sagIndex';
}

interface TestResultSectionDef {
  title?: string;
  fields: TestResultFieldDef[];
}

interface TestResultCell {
  status: string | null;
  inputValues: ResultValueMap;
  outputValues: ResultValueMap;
  inputFields: ContentFieldDto[];
  outputFields: ContentFieldDto[];
}

interface TestResultRow {
  groupKey: string;
  groupName: string;
  testTypeId: number;
  testTypeName: string;
  testTypeAlias: ResultAlias | null;
  cellByDesign: Map<number, TestResultCell>;
}

interface TestResultGroup {
  groupKey: string;
  groupName: string;
  rows: TestResultRow[];
}

const DEFAULT_RESULTS_GROUP_KEY = 'default:testresults';

/** TestInputStatuses.Id for Cancelled. Cancelled tests (deleted from the mud check) are never shown. */
const CANCELLED_TEST_INPUT_STATUS_ID = 5;

const TEST_ROW_PRIORITY: Partial<Record<ResultAlias, number>> = {
  pycnometer: 10,
  density: 10,
  hphtRheology: 15,
  rheology: 20,
  electricalStability: 30,
  retort: 40,
  psd: 50,
  hphtFluidLoss: 60,
  ppt: 70,
  apiFluidLoss: 80,
  salinity: 90,
  wbmTitration: 100,
  wbmSalinity: 110,
  staticAging: 120
};

const WATER_DENSITY_PPG = 8.345;

const SIMPLE_RESULT_SCHEMAS: Partial<Record<ResultAlias, TestResultSectionDef[]>> = {
  pycnometer: [
    {
      title: 'Pycnometer',
      fields: [
        { label: 'Density, lb/bbl', compute: 'pycnometerAverageDensity' }
      ]
    }
  ],
  density: [
    {
      title: 'Density',
      fields: [
        { label: 'Density, lb/bbl', compute: 'densityLbPerBbl' }
      ]
    }
  ],
  retort: [
    {
      title: 'Retort',
      fields: [
        { label: 'OWR', compute: 'retortOwr' }
      ]
    }
  ],
  hphtFluidLoss: [
    {
      title: 'HPHT',
      fields: [
        { label: 'Filtrate, ml', keys: ['filtrate30Min', 'filtrateVolume'], decimals: 1 },
        { label: 'Water, ml', keys: ['waterVolume'], decimals: 1 },
        { label: 'Cake, 32nds of an inch', keys: ['cakethickness'] }
      ]
    }
  ],
  ppt: [
    {
      title: 'PPT',
      fields: [
        { label: 'Spurt, ml', compute: 'pptSpurt', decimals: 1 },
        { label: 'Value, ml', compute: 'pptValue', decimals: 1 },
        { label: 'Cake, 32nds of an inch', keys: ['cakeThickness', 'cake32nds', 'cake32ndsofaninch'] }
      ]
    }
  ],
  apiFluidLoss: [
    {
      title: 'API Fluid Loss',
      fields: [
        { label: 'Filtrate, ml', keys: ['filtrateVolume', 'filtratevolumelimit'] },
        { label: 'Cake, 32nds of an inch', keys: ['cakeThickness'] }
      ]
    }
  ],
  psd: [
    {
      title: 'PSD',
      fields: [
        { label: 'D50, um', compute: 'd50' },
      ]
    }
  ],
  salinity: [
    {
      title: 'NAF Chemistry',
      fields: [
        { label: 'Alkalinity, POM', compute: 'salinityAlkalinity' },
        { label: 'Chlorides, mg/l', compute: 'salinityChlorides' },
        { label: 'Calcium, mg/l', compute: 'salinityCalcium' }
      ]
    }
  ],
  wbmTitration: [
    {
      title: 'WBM Chemistry',
      fields: [
        { label: 'Total Chlorides, mg/l', compute: 'wbmChemistryTotalChlorides' }
      ]
    }
  ],
  wbmSalinity: [
    {
      title: 'S3/S4 Chlorides',
      fields: [
        { label: 'Total Chlorides, g/ml', compute: 'wbmTotalChlorides' }
      ]
    }
  ],
  staticAging: [
    {
      title: 'Static Aging',
      fields: [
        { label: 'Mud Density, lb/gal', keys: ['initialDensity', 'initialSpecificGravity'] },
        { label: 'SAG Factor', compute: 'sagFactor'},
        { label: 'SAG Index', compute: 'sagIndex' },
      ]
    }
  ],
  hphtRheology: [
    {
      title: 'FANN 77',
      fields: [
        
      ]
    }
  ]
};

const RHEOLOGY_LINES = [
  { key: 'rpm600', label: '600 RPM, deg' },
  { key: 'rpm300', label: '300 RPM, deg' },
  { key: 'pv', label: 'PV, cps', bold: true },
  { key: 'yp', label: 'YP, lb/100ft²', bold: true }
];

@Component({
  selector: 'app-test-results',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './test-results.component.html',
  styleUrl: './test-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestResultsComponent implements OnChanges {
  @Input() designs: DesignIterationDto[] = [];
  @Input() mudCheckDesign: DesignIterationDto | null = null;
  @Input() isCutback = false;

  protected groups: TestResultGroup[] = [];

  ngOnChanges(): void {
    this.buildGroups();
  }

  private allDesignsIncludingMudCheck(): DesignIterationDto[] {
    return this.mudCheckDesign ? [this.mudCheckDesign, ...this.designs] : this.designs;
  }

  private buildGroups(): void {
    const rowMap = new Map<string, {
      groupKey: string;
      groupName: string;
      testTypeId: number;
      name: string;
      alias: ResultAlias | null;
    }>();
    const orderedKeys: string[] = [];

    for (const design of this.allDesignsIncludingMudCheck()) {
      for (const group of design.testGroups) {
        const groupName = group.name?.trim() ?? '';
        const groupKey = this.getGroupKey(groupName, design);

        for (const ti of group.testInputs) {
          if (this.isCancelledTestInput(ti)) {
            continue;
          }

          const alias = this.resolveAlias(ti.testTypeAlias, ti.testTypeName, ti.title);
          const rowKey = this.getRowKey(groupKey, ti.testTypeId, alias);

          if (!rowMap.has(rowKey)) {
            rowMap.set(rowKey, {
              groupKey,
              groupName: groupKey === DEFAULT_RESULTS_GROUP_KEY ? '' : groupName,
              testTypeId: ti.testTypeId,
              name: this.resolveDisplayName(alias, ti.testTypeName, ti.testTypeId),
              alias,
            });
            orderedKeys.push(rowKey);
          } else {
            const existing = rowMap.get(rowKey)!;
            const resolvedAlias = this.resolveAlias(ti.testTypeAlias, ti.testTypeName, ti.title);
            if (!existing.alias && resolvedAlias) {
              existing.alias = resolvedAlias;
            }
            if (ti.testTypeName?.trim()) {
              existing.name = this.resolveDisplayName(existing.alias, ti.testTypeName, ti.testTypeId);
            }
          }
        }
      }
    }

    const rows = orderedKeys.map(rowKey => {
      const meta = rowMap.get(rowKey)!;
      const cellByDesign = new Map<number, TestResultCell>();

      for (const design of this.allDesignsIncludingMudCheck()) {
        const testInput = this.findTestInput(design, meta.groupKey, meta.testTypeId, meta.alias);
        cellByDesign.set(design.id, this.buildCell(testInput, meta.alias));
      }

      return {
        groupKey: meta.groupKey,
        groupName: meta.groupName,
        testTypeId: meta.testTypeId,
        testTypeName: meta.name,
        testTypeAlias: meta.alias,
        cellByDesign,
      };
    });

    const rowsWithValues = rows.filter(row => this.rowHasAnyDisplayValue(row));

    const grouped = new Map<string, TestResultGroup>();
    for (const row of rowsWithValues) {
      let group = grouped.get(row.groupKey);
      if (!group) {
        group = {
          groupKey: row.groupKey,
          groupName: row.groupName,
          rows: []
        };
        grouped.set(row.groupKey, group);
      }

      group.rows.push(row);
    }

    for (const group of grouped.values()) {
      group.rows.sort((a, b) => {
        const aPriority = a.testTypeAlias ? (TEST_ROW_PRIORITY[a.testTypeAlias] ?? 1000) : 1000;
        const bPriority = b.testTypeAlias ? (TEST_ROW_PRIORITY[b.testTypeAlias] ?? 1000) : 1000;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return a.testTypeId - b.testTypeId;
      });
    }

    this.groups = Array.from(grouped.values());
  }

  private getGroupKey(groupName: string, design: DesignIterationDto): string {
    if (this.isCutback && design.id === this.mudCheckDesign?.id) {
      return DEFAULT_RESULTS_GROUP_KEY;
    }
    if (groupName.length > 0) {
      return `name:${groupName.toLocaleLowerCase()}`;
    }
    return DEFAULT_RESULTS_GROUP_KEY;
  }

  private getRowKey(groupKey: string, testTypeId: number, alias: ResultAlias | null): string {
    if (groupKey === DEFAULT_RESULTS_GROUP_KEY && alias) {
      return `${groupKey}::alias:${alias}`;
    }

    return `${groupKey}::${testTypeId}`;
  }

  private findTestInput(
    design: DesignIterationDto,
    groupKey: string,
    testTypeId: number,
    alias: ResultAlias | null
  ): DesignTestInputDto | undefined {
    for (const group of design.testGroups) {
      const currentGroupName = group.name?.trim() ?? '';
      if (this.getGroupKey(currentGroupName, design) !== groupKey) {
        continue;
      }

      const found = group.testInputs.find(ti => ti.testTypeId === testTypeId && !this.isCancelledTestInput(ti));
      if (found) return found;

      if (groupKey === DEFAULT_RESULTS_GROUP_KEY && alias) {
        const foundByAlias = group.testInputs.find(ti => !this.isCancelledTestInput(ti) && this.resolveAlias(ti.testTypeAlias, ti.testTypeName, ti.title) === alias);
        if (foundByAlias) return foundByAlias;
      }
    }
    return undefined;
  }

  protected colspan(): number {
    return this.designs.length + 1 + (this.showBaselineColumn() ? 1 : 0);
  }

  protected showBaselineColumn(): boolean {
    return this.mudCheckDesign !== null;
  }

  protected hasCellData(row: TestResultRow, designId: number): boolean {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return false;

    if (this.isNotStartedStatus(cell.status)) return false;

    return this.hasAnyMeaningfulValue(cell);
  }

  protected sectionsForRow(row: TestResultRow): TestResultSectionDef[] {
    return row.testTypeAlias ? (SIMPLE_RESULT_SCHEMAS[row.testTypeAlias] ?? []) : [];
  }

  protected showSimpleSections(row: TestResultRow): boolean {
    return !this.isRheology(row)
      && !this.isElectricalStability(row)
      && this.sectionsForRow(row).length > 0;
  }

  protected fieldValue(row: TestResultRow, designId: number, field: TestResultFieldDef): string { 
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    const raw = field.compute
      ? this.computeValue(cell, field.compute)
      : this.lookupValue(cell, field.keys ?? []);
    return field.decimals === undefined ? raw : this.formatDecimals(raw, field.decimals);
  }

  protected cellMeta(row: TestResultRow, designId: number): string {    
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    if (row.testTypeAlias === 'hphtFluidLoss') {
      return this.joinMeta([
        this.formatMetaValue(this.lookupValue(cell, ['temperature']), '°F'),
        this.formatMetaValue(this.lookupValue(cell, ['pressure']), 'psi')
      ]);
    }

    if (row.testTypeAlias === 'ppt') {
      const disc = this.lookupFromMaps([cell.outputValues, cell.inputValues], [
        'discGrade',
        'diskGrade',
        'discSize',
        'discSizeUm',
        'discMicronSize',
        'meshSize',
        'spurtDiscGrade'
      ]);
      const temperature = this.lookupFromMaps([cell.outputValues, cell.inputValues], [
        'temperature',
        'temperatureF',
        'testTemperature',
        'temperatureRequested'
      ]);
      const pressure = this.lookupFromMaps([cell.outputValues, cell.inputValues], [
        'pressure',
        'pressurePsi',
        'testPressure',
        'pressureApplied'
      ]);
      return this.joinMeta([
        disc + "um",
        this.formatMetaValue(temperature, '°F'),
        this.formatMetaValue(pressure, 'psi')
      ]);
    }

    return '';
  }

  protected isRheology(row: TestResultRow): boolean {
    return row.testTypeAlias === 'rheology';
  }

  protected isElectricalStability(row: TestResultRow): boolean {
    return row.testTypeAlias === 'electricalStability';
  }

  protected isHphtRheology(row: TestResultRow): boolean {
    return row.testTypeAlias === 'hphtRheology';
  }

  protected isPycnometer(row: TestResultRow): boolean {
    return row.testTypeAlias === 'pycnometer';
  }

  protected hasOutputData(row: TestResultRow, designId: number): boolean {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return false;
    if (this.isNotStartedStatus(cell.status)) return false;

    return this.hasAnyMeaningfulOutput(cell);
  }

  private rowHasAnyDisplayValue(row: TestResultRow): boolean {
    for (const design of this.allDesignsIncludingMudCheck()) {
      if (this.hasCellData(row, design.id)) {
        return true;
      }
    }

    return false;
  }

  private hasAnyMeaningfulValue(cell: TestResultCell): boolean {
    return this.hasAnyMeaningfulOutput(cell)
      || this.hasAnyNonEmptyMapValue(cell.inputValues)
      || this.hasAnyNonEmptyFieldValue(cell.inputFields);
  }

  private hasAnyMeaningfulOutput(cell: TestResultCell): boolean {
    return this.hasAnyNonEmptyMapValue(cell.outputValues)
      || this.hasAnyNonEmptyFieldValue(cell.outputFields);
  }

  private hasAnyNonEmptyMapValue(map: ResultValueMap): boolean {
    for (const value of map.values()) {
      if ((value ?? '').trim().length > 0) {
        return true;
      }
    }

    return false;
  }

  private hasAnyNonEmptyFieldValue(fields: ContentFieldDto[]): boolean {
    for (const field of fields) {
      if ((field.value ?? '').trim().length > 0) {
        return true;
      }
    }

    return false;
  }

  protected rheologyLines(): Array<{ key: string; label: string; bold?: boolean }> {
    return RHEOLOGY_LINES;
  }

  protected rheologyTemperatures(row: TestResultRow, designId: number): number[] {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return [];

    const temperatures = new Set<number>();
    for (const key of [...cell.inputValues.keys(), ...cell.outputValues.keys()]) {
      const match = /^t(\d+)_/.exec(key);
      if (!match) continue;
      temperatures.add(Number(match[1]));
    }

    if (temperatures.size === 0) {
      for (const field of cell.inputFields) {
        if (this.normalizeKey(field.label) !== 'temperature') {
          continue;
        }

        const parsed = Number(field.value);
        if (Number.isFinite(parsed)) {
          temperatures.add(parsed);
        }
      }
    }

    return Array.from(temperatures)
      .filter(Number.isFinite)
      .sort((a, b) => a - b)
      .filter(temperature =>
        this.rheologyLines().some(line => this.rheologyCellValue(row, designId, temperature, line.key) !== '')
      );
  }

  protected rheologyCellValue(row: TestResultRow, designId: number, temperature: number, key: string): string {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    const keyedNumber = (rowKey: string): number | null => {
      const fromMap = this.numberLookup(cell.outputValues, [`t${temperature}_${rowKey}`]);
      if (fromMap !== null) {
        return fromMap;
      }

      const fallbackByTemp = this.rheologyFallbackValuesByTemperature(cell).get(temperature);
      if (!fallbackByTemp) {
        return null;
      }

      const labelMap: Record<string, string> = {
        rpm600: 'viscosity600rpm',
        rpm300: 'viscosity300rpm',
        rpm200: 'viscosity200rpm',
        rpm100: 'viscosity100rpm',
        rpm6: 'viscosity6rpm',
        rpm3: 'viscosity3rpm',
        gel10s: 'gel10sec',
        gel10m: 'gel10min',
        gel30m: 'gel30min'
      };

      const fallbackRaw = fallbackByTemp.get(labelMap[rowKey] ?? this.normalizeKey(rowKey));
      if (!fallbackRaw) {
        return null;
      }

      const parsed = Number(fallbackRaw);
      return Number.isFinite(parsed) ? parsed : null;
    };

    if (key === 'pv') {
      const rpm600 = keyedNumber('rpm600');
      const rpm300 = keyedNumber('rpm300');
      if (rpm600 === null || rpm300 === null) return '';
      return String(rpm600 - rpm300);
    }

    if (key === 'yp') {
      const rpm300 = keyedNumber('rpm300');
      const pv = this.rheologyCellValue(row, designId, temperature, 'pv');
      const numericPv = Number(pv);
      if (rpm300 === null || !Number.isFinite(numericPv)) return '';
      return String(rpm300 - numericPv);
    }

    const fromMap = this.value(cell.outputValues, `t${temperature}_${key}`);
    if (fromMap) {
      return fromMap;
    }

    const fallbackNumber = keyedNumber(key);
    return fallbackNumber === null ? '' : String(fallbackNumber);
  }

  private rheologyFallbackValuesByTemperature(cell: TestResultCell): Map<number, Map<string, string>> {
    const byTemperature = new Map<number, Map<string, string>>();
    const idToTemperature = new Map<string, number>();

    let currentInputId = '';
    for (const field of cell.inputFields) {
      const normalizedLabel = this.normalizeKey(field.label);
      if (normalizedLabel === 'id') {
        currentInputId = field.value?.trim() ?? '';
        continue;
      }

      if (normalizedLabel !== 'temperature' || !currentInputId) {
        continue;
      }

      const parsedTemperature = Number(field.value);
      if (Number.isFinite(parsedTemperature)) {
        idToTemperature.set(currentInputId, parsedTemperature);
      }
    }

    let currentOutputId = '';
    for (const field of cell.outputFields) {
      const normalizedLabel = this.normalizeKey(field.label);
      if (normalizedLabel === 'id') {
        currentOutputId = field.value?.trim() ?? '';
        continue;
      }

      if (!currentOutputId) {
        continue;
      }

      const temperature = idToTemperature.get(currentOutputId);
      if (temperature === undefined) {
        continue;
      }

      let valuesAtTemp = byTemperature.get(temperature);
      if (!valuesAtTemp) {
        valuesAtTemp = new Map<string, string>();
        byTemperature.set(temperature, valuesAtTemp);
      }

      valuesAtTemp.set(normalizedLabel, field.value ?? '');
    }

    return byTemperature;
  }

  protected electricalStabilityReadings(row: TestResultRow, designId: number): Array<{ index: number; temperature: number }> {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return [];

    const indices = new Set<number>();
    for (const key of [...cell.inputValues.keys(), ...cell.outputValues.keys()]) {
      const match = /^esreading(\d+)(id|temperature|voltage1|voltage2|voltage3)$/.exec(key);
      if (!match) continue;
      indices.add(Number(match[1]));
    }

    return Array.from(indices)
      .filter(Number.isFinite)
      .sort((a, b) => a - b)
      .map(index => ({
        index,
        temperature: this.numberLookup(cell.inputValues, [`esReading${index}Temperature`, `requestedTemperature`]) ?? 0
      }));
  }

  protected electricalStabilityAverage(row: TestResultRow, designId: number, index: number): string {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    const voltages = [1, 2, 3]
      .map(voltageIndex => this.numberLookup(cell.outputValues, [`esReading${index}Voltage${voltageIndex}`]))
      .filter((value): value is number => value !== null);

    if (voltages.length === 0) return '';
    return String(Math.trunc(voltages.reduce((sum, current) => sum + current, 0) / voltages.length));
  }

  protected pycnometerRuns(): number[] {
    return [1, 2, 3, 4];
  }

  protected pycnometerValue(row: TestResultRow, designId: number, key: string): string {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';
    return this.lookupValue(cell, [key]);
  }

  protected pycnometerRunVp(row: TestResultRow, designId: number, run: number): string {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    const cellVolume = this.numberLookup(cell.outputValues, ['cellVolume']);
    const referenceVolume = this.numberLookup(cell.outputValues, ['referenceVolume']);
    const p1 = this.numberLookup(cell.outputValues, [`run${run}_p1`]);
    const p2 = this.numberLookup(cell.outputValues, [`run${run}_p2`]);
    if (cellVolume === null || referenceVolume === null || p1 === null || p2 === null || p2 === 0) {
      return '';
    }

    const volumeOfPowder = cellVolume - referenceVolume * (p1 / p2 - 1);
    return Number.isFinite(volumeOfPowder) ? volumeOfPowder.toFixed(3) : '';
  }

  protected pycnometerRunSg(row: TestResultRow, designId: number, run: number): string {
    const cell = row.cellByDesign.get(designId);
    if (!cell) return '';

    const sampleWeight = this.numberLookup(cell.outputValues, ['sampleWeight']);
    const vp = Number(this.pycnometerRunVp(row, designId, run));
    if (sampleWeight === null || !Number.isFinite(vp) || vp <= 0) {
      return '';
    }

    return (sampleWeight / vp).toFixed(3);
  }

  protected genericFallbackFields(row: TestResultRow, designId: number): ContentFieldDto[] {
    const cell = row.cellByDesign.get(designId);
    return cell?.outputFields ?? [];
  }

  private buildCell(testInput: DesignTestInputDto | undefined, alias: ResultAlias | null): TestResultCell {
    const defaultOutput = testInput?.testOutputs.find(o => o.isDefault) ?? testInput?.testOutputs[0] ?? null;
    const inputValues = this.parseContent(testInput?.content ?? null, alias, 'input');
    const outputValues = this.parseContent(defaultOutput?.content ?? null, alias, 'output', testInput?.content ?? null);

    this.mergeFieldFallback(inputValues, testInput?.parsedInputValues ?? []);
    this.mergeFieldFallback(outputValues, defaultOutput?.parsedValues ?? []);

    if (defaultOutput?.equipmentName) {
      outputValues.set(this.normalizeKey('equipmentName'), defaultOutput.equipmentName);
    }

    return {
      status: testInput?.status ?? null,
      inputValues,
      outputValues,
      inputFields: testInput?.parsedInputValues ?? [],
      outputFields: defaultOutput?.parsedValues ?? []
    };
  }

  private isCancelledTestInput(testInput: DesignTestInputDto): boolean {
    return testInput.statusId === CANCELLED_TEST_INPUT_STATUS_ID;
  }

  private isNotStartedStatus(status: string | null | undefined): boolean {
    //return false; // for debugging purposes
    if (!status) return false;
    return status.toLowerCase().replace(/[^a-z0-9]/g, '') === 'notstarted';
  }

  private resolveAlias(
    alias: string | null | undefined,
    testTypeName: string | null | undefined,
    title?: string | null | undefined
  ): ResultAlias | null {
    const candidates = [alias, testTypeName, title]
      .map(value => (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, ''))
      .filter(value => value.length > 0);

    if (candidates.length === 0) return null;

    for (const value of candidates) {
      if (value.includes('pycnometer')) return 'pycnometer';
    }
    
    for (const value of candidates) {
      if (value.includes('density')) return 'density';
      if (value.includes('hphtrheology') || value.includes('fann77')) return 'hphtRheology';
      if (value.includes('rheology')) return 'rheology';
      if (value.includes('electricalstability')) return 'electricalStability';
      if (value.includes('hphtfluidloss') || value.includes('hthp')) return 'hphtFluidLoss';
      if (value.includes('apifluidloss')) return 'apiFluidLoss';
      if (value.includes('permeabilityplugging') || value.includes('ppt')) return 'ppt';
      if (value.includes('retort')) return 'retort';
      if (value.includes('particlesize') || value.includes('psd')) return 'psd';
      if (value.includes('wbmchemistry')) return 'wbmSalinity';
      if (value.includes('wbmchlorides') || value.includes('wbmtitration')) return 'wbmTitration';
      if (value.includes('wbmsalinity') || value.includes('s3s4chlorides')) return 'wbmSalinity';
      if (value.includes('nafchemistry') || value.includes('salinity')) return 'salinity';
      if (value.includes('staticaging')) return 'staticAging';
    }

    return null;
  }

  private resolveDisplayName(alias: ResultAlias | null, rawName: string | null | undefined, testTypeId: number): string {
    if (alias === 'pycnometer') return 'Pycnometer';
    if (alias === 'ppt') return 'PPT';
    if (alias === 'electricalStability') return 'Electrical Stability';
    return rawName ?? String(testTypeId);
  }

  private parseContent(
    content: string | null,
    alias: ResultAlias | null,
    kind: 'input' | 'output',
    inputContent?: string | null
  ): ResultValueMap {
    const values = new Map<string, string>();
    if (!content) return values;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.getElementsByTagName('parsererror').length > 0) {
      return values;
    }

    const root = document.documentElement;
    if (!root) return values;

    if (alias === 'rheology') {
      if (kind === 'input') {
        this.parseRheologyInput(root, values);
      } else {
        this.parseRheologyOutput(root, this.extractRheologyTemperatures(inputContent), values);
      }
      return values;
    }

    if (alias === 'electricalStability') {
      if (kind === 'input') {
        this.parseElectricalStabilityInput(root, values);
      } else {
        this.parseElectricalStabilityOutput(root, values);
      }
      return values;
    }

    if (alias === 'pycnometer') {
      this.parsePycnometer(root, values);
      return values;
    }

    if (alias === 'psd') {
      this.parsePsd(root, values);
      return values;
    }

    this.collectLeafValues(root, values);
    return values;
  }

  private collectLeafValues(element: Element, values: ResultValueMap): void {
    const children = Array.from(element.children);
    if (children.length === 0) {
      const text = element.textContent?.trim() ?? '';
      if (!text) return;

      if (element.tagName.toLowerCase() === 'parameter') {
        const key = element.getAttribute('key')?.trim();
        if (key) {
          values.set(this.normalizeKey(key), text);
          return;
        }
      }

      values.set(this.normalizeKey(element.tagName), text);
      return;
    }

    for (const child of children) {
      this.collectLeafValues(child, values);
    }
  }

  private parsePycnometer(root: Element, values: ResultValueMap): void {
    for (const key of ['ProfileName', 'ReferenceVolume', 'CellVolume', 'SampleWeight']) {
      const text = root.querySelector(key)?.textContent?.trim();
      if (text) {
        values.set(this.normalizeKey(key), text);
      }
    }

    const readings = Array.from(root.querySelectorAll(':scope > Readings > *'));
    readings.slice(0, 4).forEach((reading, index) => {
      const run = index + 1;
      const p1 = reading.querySelector('P1')?.textContent?.trim();
      const p2 = reading.querySelector('P2')?.textContent?.trim();
      if (p1) values.set(this.normalizeKey(`run${run}_p1`), p1);
      if (p2) values.set(this.normalizeKey(`run${run}_p2`), p2);
    });
  }

  private parseRheologyInput(root: Element, values: ResultValueMap): void {
    const readings = Array.from(root.querySelectorAll(':scope > Readings > *'));
    for (const reading of readings) {
      const temperature = reading.querySelector('Temperature')?.textContent?.trim();
      const pressure = reading.querySelector('Pressure')?.textContent?.trim();
      if (!temperature) continue;
      if (pressure) {
        values.set(this.normalizeKey(`t${temperature}_pressure`), pressure);
      }
    }
  }

  private parseRheologyOutput(root: Element, temperatures: number[], values: ResultValueMap): void {
    const readings = Array.from(root.querySelectorAll(':scope > Readings > *'));
    readings.forEach((reading, index) => {
      const temperature = temperatures[index] ?? [80, 120, 150][index];
      if (!temperature) return;

      const prefix = `t${temperature}_`;
      this.setQueryValue(values, `${prefix}rpm600`, reading, 'Viscosity600Rpm');
      this.setQueryValue(values, `${prefix}rpm300`, reading, 'Viscosity300Rpm');
      this.setQueryValue(values, `${prefix}rpm200`, reading, 'Viscosity200Rpm');
      this.setQueryValue(values, `${prefix}rpm100`, reading, 'Viscosity100Rpm');
      this.setQueryValue(values, `${prefix}rpm6`, reading, 'Viscosity6Rpm');
      this.setQueryValue(values, `${prefix}rpm3`, reading, 'Viscosity3Rpm');
      this.setQueryValue(values, `${prefix}gel10s`, reading, 'Gel10Sec');
      this.setQueryValue(values, `${prefix}gel10m`, reading, 'Gel10Min');
      this.setQueryValue(values, `${prefix}gel30m`, reading, 'Gel30Min');
    });
  }

  private extractRheologyTemperatures(content: string | null | undefined): number[] {
    if (!content) return [];
    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.getElementsByTagName('parsererror').length > 0) {
      return [];
    }

    const root = document.documentElement;
    if (!root) {
      return [];
    }

    return Array.from(root.querySelectorAll(':scope > Readings > *'))
      .map(reading => Number(reading.querySelector('Temperature')?.textContent?.trim()))
      .filter(Number.isFinite);
  }

  private parseElectricalStabilityInput(root: Element, values: ResultValueMap): void {
    const readings = Array.from(root.querySelectorAll(':scope > Readings > *'));
    readings.forEach((reading, index) => {
      const readingIndex = index + 1;
      const temperature = reading.querySelector('Temperature')?.textContent?.trim();
      if (temperature) {
        values.set(this.normalizeKey(`esReading${readingIndex}Temperature`), temperature);
        if (readingIndex === 1) {
          values.set(this.normalizeKey('requestedTemperature'), temperature);
        }
      }
    });
  }

  private parseElectricalStabilityOutput(root: Element, values: ResultValueMap): void {
    const readings = Array.from(root.querySelectorAll(':scope > Readings > *'));
    readings.forEach((reading, index) => {
      const readingIndex = index + 1;
      const voltages = Array.from(reading.querySelectorAll('int, Int32, Voltage, DielectricBreakdownVoltage'));
      if (voltages.length === 0) {
        const nested = Array.from(reading.children).find(child => child.tagName.toLowerCase().includes('dielectric'));
        if (nested) {
          Array.from(nested.children).forEach((child, voltageIndex) => {
            const text = child.textContent?.trim();
            if (text) {
              values.set(this.normalizeKey(`esReading${readingIndex}Voltage${voltageIndex + 1}`), text);
            }
          });
        }
      } else {
        voltages.slice(0, 3).forEach((voltage, voltageIndex) => {
          const text = voltage.textContent?.trim();
          if (text) {
            values.set(this.normalizeKey(`esReading${readingIndex}Voltage${voltageIndex + 1}`), text);
          }
        });
      }
    });
  }

  private parsePsd(root: Element, values: ResultValueMap): void {
    const percentiles = Array.from(root.querySelectorAll(':scope > Percentiles > *'));
    for (const percentile of percentiles) {
      const percentileValue = percentile.querySelector('Percentile')?.textContent?.trim();
      const sizeValue = percentile.querySelector('Size')?.textContent?.trim();
      if (!percentileValue || !sizeValue) continue;
      values.set(this.normalizeKey(`percentile${percentileValue}_size`), sizeValue);
      if (percentileValue === '10' || percentileValue === '50' || percentileValue === '90') {
        values.set(this.normalizeKey(`d${percentileValue}`), sizeValue);
      }
    }

    const sizePercents = Array.from(root.querySelectorAll(':scope > SizePercents > *'));
    for (const sizePercent of sizePercents) {
      const size = sizePercent.querySelector('Size')?.textContent?.trim();
      const percentileValue = sizePercent.querySelector('Percentile')?.textContent?.trim();
      if (!size || !percentileValue) continue;
      values.set(this.normalizeKey(`size${size}_percentile`), percentileValue);
    }
  }

  private setQueryValue(values: ResultValueMap, key: string, element: Element, selector: string): void {
    const text = element.querySelector(selector)?.textContent?.trim();
    if (text) {
      values.set(this.normalizeKey(key), text);
    }
  }

  private mergeFieldFallback(values: ResultValueMap, fields: ContentFieldDto[]): void {
    for (const field of fields) {
      if (!field.value) continue;
      const normalized = this.normalizeKey(field.label);
      if (!values.has(normalized)) {
        values.set(normalized, field.value);
      }
    }
  }

  private lookupValue(cell: TestResultCell, keys: string[]): string {
    return this.lookupFromMaps([cell.outputValues, cell.inputValues], keys);
  }

  private lookupFromMaps(maps: ResultValueMap[], keys: string[]): string {
    for (const key of keys) {
      const normalized = this.normalizeKey(key);
      for (const map of maps) {
        const value = map.get(normalized)?.trim() ?? '';
        if (value) {
          return value;
        }
      }
    }

    return '';
  }

  private value(map: ResultValueMap, key: string): string {
    return map.get(this.normalizeKey(key)) ?? '';
  }

  private numberLookup(map: ResultValueMap, keys: string[]): number | null {
    for (const key of keys) {
      const raw = map.get(this.normalizeKey(key));
      if (!raw) continue;
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  private computeValue(cell: TestResultCell, compute: TestResultFieldDef['compute']): string {
    switch (compute) {
      case 'sagFactor': {
        const topDensity = this.numberLookup(cell.outputValues, ["topspecificgravity"]) ;
        const bottomDensity = this.numberLookup(cell.outputValues, ['bottomspecificgravity']) ;
        return topDensity + bottomDensity > 0
          ? (bottomDensity / (topDensity + bottomDensity)).toFixed(2)
          : '';
      }
      case 'sagIndex': {
        const initialDensity = this.numberLookup(cell.outputValues, ["initialspecificgravity"]) ;
        const bottomDensity = this.numberLookup(cell.outputValues, ['bottomspecificgravity']) ;
        return initialDensity > 0 && bottomDensity > 0
          ? (bottomDensity / (2 * initialDensity)).toFixed(2)
          : '';
      }
      case 'densityLbPerBbl': {
        const explicit = this.lookupFromMaps([cell.outputValues], ['densityPpg']);
        if (explicit) {
          const parsed = Number(explicit);
          return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(3) : explicit;
        }
        const sg = this.numberLookup(cell.outputValues, ['specificGravity']);
        return sg && sg > 0 ? (sg * WATER_DENSITY_PPG).toFixed(3) : '';
      }
      case 'retortOwr': {
        const total = this.numberLookup(cell.outputValues, ['totalVolume', 'sampleVolumeMl']);
        const water = this.numberLookup(cell.outputValues, ['waterVolume']);
        if (total === null || water === null || total <= 0 || water < 0 || water > total) return '';
        const oil = total - water;
        const liquid = oil + water;
        if (liquid <= 0) return '';
        const oilPct = Math.round((oil / liquid) * 100);
        const waterPct = Math.round((water / liquid) * 100);
        return `${oilPct} / ${waterPct}`;
      }
      case 'hphtFullFiltrate': {
        const filtrate = this.numberLookup(cell.outputValues, ['filtrate30Min', 'filtrateVolume']);
        return filtrate === null ? '' : (filtrate * 2).toFixed(3);
      }
      case 'pptSpurt': {
        const firstTube = this.numberLookup(cell.outputValues, ['firsttubevolume']);
        const secondTube = this.numberLookup(cell.outputValues, ['secondtubevolume']);
        if (firstTube === null || secondTube === null || firstTube < 0 || secondTube < 0) return '';
        const totalVolume = firstTube + secondTube;
        const multipliedFirstVolume = firstTube * 2;
        const spurt = multipliedFirstVolume < totalVolume ? 0 : 2 * (multipliedFirstVolume - totalVolume);
        return spurt.toFixed(3);
      }
      case 'pptTotal': {
        const firstTube = this.numberLookup(cell.outputValues, ['firsttubevolume']);
        const secondTube = this.numberLookup(cell.outputValues, ['secondtubevolume']);
        if (firstTube === null || secondTube === null || firstTube < 0 || secondTube < 0) return '';
        return (firstTube + secondTube).toFixed(3);
      }
      case 'pptValue': {
        const firstTube = this.numberLookup(cell.outputValues, ['firsttubevolume']);
        const secondTube = this.numberLookup(cell.outputValues, ['secondtubevolume']);
        if (firstTube === null || secondTube === null || firstTube < 0 || secondTube < 0) return '';
        const totalVolume = firstTube + secondTube;
        return (totalVolume * 2).toFixed(3);
      }
      case 'salinityAlkalinity': {
        const alkalineDemand = this.numberLookup(cell.outputValues, ['alkalineDemand', 'alkalineDemandVolume']);
        const acidVolume = this.numberLookup(cell.outputValues, ['acidVolume']);
        const mudUsedVolume = this.numberLookup(cell.outputValues, ['mudUsedVolume', 'sampleWeight']);
        if (alkalineDemand === null || acidVolume === null || mudUsedVolume === null || mudUsedVolume <= 0) return '';
        return ((alkalineDemand + acidVolume) / mudUsedVolume).toFixed(2);
      }
      case 'salinityChlorides': {
        const agNo3 = this.numberLookup(cell.outputValues, ['agNo3Volume', 'silverNitrateVolume']);
        const concentration = this.numberLookup(cell.outputValues, ['agNo3Concentration', 'silverNitrateConcentration']);
        const mudUsedVolume = this.numberLookup(cell.outputValues, ['mudUsedVolume', 'sampleWeight']);
        if (agNo3 === null || concentration === null || mudUsedVolume === null || mudUsedVolume <= 0) return '';
        return ((agNo3 * concentration) / mudUsedVolume).toFixed(0);
      }
      case 'salinityCalcium': {
        const edta = this.numberLookup(cell.outputValues, ['edtaVolume']);
        const concentration = this.numberLookup(cell.outputValues, ['edtaConcentration']);
        const mudUsedVolume = this.numberLookup(cell.outputValues, ['mudUsedVolume', 'sampleWeight']);
        if (edta === null || concentration === null || mudUsedVolume === null || mudUsedVolume <= 0) return '';
        return ((edta * concentration) / mudUsedVolume).toFixed(0);
      }
      case 'wbmChemistryTotalChlorides': {
        const silverNitrateVolume = this.numberLookup(cell.inputValues, [
          'agNo3Volume',
          'wholeMudSilverNitrateVolume',
          'silverNitrateVolume'
        ]);
        const concentration = this.numberLookup(cell.inputValues, [
          'wholeMudSilverNitrateConcentration',
          'agNo3Concentration',
          'silverNitrateConcentration'
        ]);
        const mudUsedVolume = this.numberLookup(cell.inputValues, [
          'mudUsedVolume',
          'wholeMudSampleVolume',
          'mudSampleVolume',
          'sampleWeight',
          'mudSampleWeight',
          'wholeMudSampleWeight'
        ]);

        if (silverNitrateVolume === null || concentration === null || mudUsedVolume === null || concentration <= 0 || mudUsedVolume <= 0) {
          return '';
        }

        return ((silverNitrateVolume * concentration) / mudUsedVolume).toFixed(0);
      }
      case 'wbmTotalChlorides': {
        const persistedTotal = this.lookupFromMaps(
          [cell.outputValues, cell.inputValues],
          ['wbmTitration_totalChlorides', 'totalChloridesGml', 'totalChlorides']
        );
        if (persistedTotal) {
          const parsedPersisted = Number(persistedTotal);
          return Number.isFinite(parsedPersisted) ? parsedPersisted.toFixed(0) : persistedTotal;
        }

        const silverNitrateVolume = this.numberLookup(cell.outputValues, ['agNo3Volume', 'wholeMudSilverNitrateVolume'])
          ?? this.numberLookup(cell.inputValues, ['agNo3Volume', 'wholeMudSilverNitrateVolume', 'silverNitrateVolume']);
        const concentration = this.numberLookup(cell.outputValues, ['agNo3Concentration', 'wholeMudSilverNitrateConcentration'])
          ?? this.numberLookup(cell.inputValues, ['agNo3Concentration', 'wholeMudSilverNitrateConcentration', 'silverNitrateConcentration']);
        const diluentVolume = this.numberLookup(cell.outputValues, ['diluentTitratedVolume', 'volumeOfDiluentTitratedMl'])
          ?? this.numberLookup(cell.inputValues, ['diluentTitratedVolume', 'volumeOfDiluentTitratedMl']);
        const flaskVolume = this.numberLookup(cell.outputValues, ['flaskVolume', 'volumeOfFlaskMl'])
          ?? this.numberLookup(cell.inputValues, ['flaskVolume', 'volumeOfFlaskMl']);
        const mudSampleWeight = this.numberLookup(cell.outputValues, ['mudSampleWeight', 'wholeMudSampleWeight'])
          ?? this.numberLookup(cell.inputValues, ['mudSampleWeight', 'wholeMudSampleWeight']);
        const mudSampleVolumeFromField = this.numberLookup(cell.outputValues, ['mudSampleVolume', 'wholeMudSampleVolume'])
          ?? this.numberLookup(cell.inputValues, ['mudSampleVolume', 'wholeMudSampleVolume']);
        const mudSpecificGravity = this.numberLookup(cell.outputValues, ['mudSpecificGravity', 'specificGravity', 'measuredSpecificGravity'])
          ?? this.numberLookup(cell.inputValues, ['mudSpecificGravity', 'specificGravity', 'measuredSpecificGravity']);
        const bariteLbPerBbl = this.numberLookup(cell.outputValues, ['bariteLbPerBbl', 'bariteSharePoundsPerBarrel'])
          ?? this.numberLookup(cell.inputValues, ['bariteLbPerBbl', 'bariteSharePoundsPerBarrel'])
          ?? 0;

        const mudSampleVolume = mudSampleVolumeFromField && mudSampleVolumeFromField > 0
          ? mudSampleVolumeFromField
          : (mudSpecificGravity && mudSpecificGravity > 0 && mudSampleWeight && mudSampleWeight > 0
            ? mudSampleWeight / mudSpecificGravity
            : null);

        if (
          silverNitrateVolume === null || concentration === null || concentration <= 0
          || diluentVolume === null || diluentVolume <= 0
          || flaskVolume === null || flaskVolume <= 0
          || mudSampleVolume === null || mudSampleVolume <= 0
          || mudSpecificGravity === null || mudSpecificGravity <= 0
          || mudSampleWeight === null || mudSampleWeight <= 0
        ) {
          const base = this.numberLookup(cell.outputValues, ['mudSampleWeight', 'mudSampleVolume', 'wholeMudSampleWeight', 'wholeMudSampleVolume'])
            ?? this.numberLookup(cell.inputValues, ['mudSampleWeight', 'mudSampleVolume', 'wholeMudSampleWeight', 'wholeMudSampleVolume']);
          if (base === null || base <= 0) return '';
          return ((silverNitrateVolume ?? 0) * (concentration ?? 0) / base).toFixed(0);
        }

        const bariteDensity = 4.2;
        const chloridesInBlend = (silverNitrateVolume * concentration) / diluentVolume;
        const bariteShareGg = bariteLbPerBbl / (1000 * mudSpecificGravity);
        const bariteInFlaskWeight = mudSampleWeight * bariteShareGg;
        const bariteInFlaskVolume = bariteInFlaskWeight / bariteDensity;
        const volumeMinusBarite = flaskVolume - bariteInFlaskVolume;
        const roundedMudSampleVolume = Math.round(mudSampleVolume * 100) / 100;
        if (roundedMudSampleVolume <= 0) return '';
        const dilutionRatio = volumeMinusBarite / roundedMudSampleVolume;
        const chloridesCorrection = ((chloridesInBlend * 2) - ((25 + diluentVolume + silverNitrateVolume) * 0.005)) / (chloridesInBlend * 2);
        const totalChlorides = dilutionRatio * chloridesInBlend * chloridesCorrection;

        return Number.isFinite(totalChlorides) ? totalChlorides.toFixed(0) : '';
      }
      case 'd10':
      case 'd50':
      case 'd90':
        return this.lookupFromMaps([cell.outputValues], [compute, `percentile${compute.slice(1)}_size`]);
      case 'pycnometerAverageSg': {
        const values = this.pycnometerRunValues(cell);
        if (values.length === 0) return '';
        return (values.reduce((sum, current) => sum + current, 0) / values.length).toFixed(3);
      }
      case 'pycnometerAverageDensity': {
        const avgSg = Number(this.computeValue(cell, 'pycnometerAverageSg'));
        return Number.isFinite(avgSg) && avgSg > 0 ? (avgSg * WATER_DENSITY_PPG).toFixed(3) : '';
      }
      case 'pycnometerAverageAbsoluteVolume': {
        const density = Number(this.computeValue(cell, 'pycnometerAverageDensity'));
        return Number.isFinite(density) && density > 0 ? (1 / density).toFixed(3) : '';
      }
      default:
        return '';
    }
  }

  private pycnometerRunValues(cell: TestResultCell): number[] {
    const sampleWeight = this.numberLookup(cell.outputValues, ['sampleWeight']);
    const cellVolume = this.numberLookup(cell.outputValues, ['cellVolume']);
    const referenceVolume = this.numberLookup(cell.outputValues, ['referenceVolume']);
    if (sampleWeight === null || cellVolume === null || referenceVolume === null) {
      return [];
    }

    return this.pycnometerRuns()
      .map(run => {
        const p1 = this.numberLookup(cell.outputValues, [`run${run}_p1`]);
        const p2 = this.numberLookup(cell.outputValues, [`run${run}_p2`]);
        if (p1 === null || p2 === null || p2 === 0) return Number.NaN;
        const vp = cellVolume - referenceVolume * (p1 / p2 - 1);
        if (!Number.isFinite(vp) || vp <= 0) return Number.NaN;
        return sampleWeight / vp;
      })
      .filter(Number.isFinite);
  }

  private normalizeKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private joinMeta(parts: string[]): string {
    return parts.filter(part => part.length > 0).join(', ');
  }

  private formatMetaValue(value: string, suffix: string): string {
    return value ? `${value}${suffix}` : '';
  }

  private formatDecimals(value: string, decimals: number): string {
    const parsed = Number(value);
    return value.trim() !== '' && Number.isFinite(parsed) ? parsed.toFixed(decimals) : value;
  }

  protected trackByDesignId(_: number, d: DesignIterationDto): number { return d.id; }
  protected trackByRow(_: number, r: TestResultRow): string { return `${r.groupKey}::${r.testTypeId}`; }
}
